/**
 * Vue Currency Input 1.7.0
 * (c) 2019 Matthias Stiller
 * @license MIT
 */
import { createTextMaskInputElement } from 'text-mask-core';
import Vue from 'vue';

var defaultOptions = {
  locale: undefined,
  currency: 'EUR',
  distractionFree: true,
  min: null,
  max: null,
  validateOnInput: false
};

var onlyDigits = function (str) { return str.replace(/\D+/g, ''); };
var endsWith = function (str, search) {
  return str.substring(str.length - search.length, str.length) === search
};
var startsWith = function (str, search) {
  return str.substring(0, search.length) === search
};
var removePrefix = function (str, prefix) {
  if (prefix && startsWith(str, prefix)) {
    return str.substr(prefix.length)
  }
  return str
};
var removeSuffix = function (str, suffix) {
  if (suffix && endsWith(str, suffix)) {
    return str.slice(0, suffix.length * -1)
  }
  return str
};
var parse = function (str, ref) {
  if ( ref === void 0 ) ref = {};
  var prefix = ref.prefix;
  var suffix = ref.suffix;
  var thousandsSeparatorSymbol = ref.thousandsSeparatorSymbol;
  var decimalSymbol = ref.decimalSymbol;
  if (typeof str === 'number') {
    return str
  } else if (str && typeof str === 'string') {
    if (str.match(/^-?\d+(\.\d+)?$/)) {
      return Number(str)
    }
    var negative = startsWith(str, '-');
    str = removePrefix(str, '-');
    str = removePrefix(str, prefix);
    str = removeSuffix(str, suffix);
    var numberParts = str.split(decimalSymbol);
    if (numberParts.length > 2) {
      return null
    }
    var integer = numberParts[0].replace(new RegExp(thousandsSeparatorSymbol === '.' ? '\\.' : thousandsSeparatorSymbol, 'g'), '');
    if (integer.length && !integer.match(/^\d+$/g)) {
      return null
    }
    var number = integer;
    if (numberParts.length === 2) {
      var fraction = numberParts[1];
      if (fraction.length && !fraction.match(/^\d+$/g)) {
        return null
      }
      number += "." + fraction;
    }
    if (number) {
      if (negative) {
        number = "-" + number;
      }
      return Number(number)
    }
  }
  return null
};

var digitRegExp = /\d/;
var caretTrap = '[]';
var convertToMask = function (strNumber) { return strNumber.split('').map(function (char) { return digitRegExp.test(char) ? digitRegExp : char; }); };
function createCurrencyMask (ref) {
  if ( ref === void 0 ) ref = {};
  var prefix = ref.prefix;
  var suffix = ref.suffix;
  var thousandsSeparatorSymbol = ref.thousandsSeparatorSymbol;
  var allowDecimal = ref.allowDecimal;
  var allowNegative = ref.allowNegative;
  var decimalSymbol = ref.decimalSymbol;
  var decimalLimit = ref.decimalLimit;
  var prefixLength = prefix ? prefix.length : 0;
  var suffixLength = suffix ? suffix.length : 0;
  return function (rawValue) {
    if ( rawValue === void 0 ) rawValue = '';
    var indexOfDecimalSymbol = rawValue.indexOf(decimalSymbol);
    var hasDecimal = indexOfDecimalSymbol !== -1;
    var negative = startsWith(rawValue, '-') && allowNegative;
    rawValue = removePrefix(rawValue, '-');
    rawValue = removePrefix(rawValue, prefix);
    rawValue = removeSuffix(rawValue, suffix);
    var integer, fraction;
    if (hasDecimal && allowDecimal) {
      var numberParts = rawValue.split(decimalSymbol);
      integer = numberParts[0];
      fraction = convertToMask(onlyDigits(numberParts[1]));
    } else {
      integer = rawValue;
    }
    integer = onlyDigits(integer);
    integer = integer.replace(/^0+(0$|[^0])/, '$1');
    integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparatorSymbol);
    var mask = convertToMask(integer);
    if (hasDecimal && allowDecimal) {
      mask.push(caretTrap, decimalSymbol, caretTrap);
      if (fraction) {
        if (typeof decimalLimit === 'number') {
          fraction = fraction.slice(0, decimalLimit);
        }
        mask = mask.concat(fraction);
      }
    }
    if (prefixLength > 0) {
      mask = prefix.split('').concat(mask);
    }
    if (negative) {
      mask = [/-/].concat(mask);
    }
    if (suffixLength > 0) {
      mask = mask.concat(suffix.split(''));
    }
    return mask
  }
}

function currencyFormatConfig (locale, currency) {
  var numberFormat = new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
  var formattedNumber = numberFormat.format(1234);
  var decimalLimit = (formattedNumber.match(/0/g) || []).length;
  var decimalSymbol = decimalLimit > 0 ? formattedNumber.substr(formattedNumber.indexOf('4') + 1, 1) : null;
  var allowDecimal = decimalSymbol !== null;
  var prefix = formattedNumber.substring(0, formattedNumber.indexOf('1'));
  var suffix = formattedNumber.substring(formattedNumber.lastIndexOf(decimalLimit > 0 ? '0' : '4') + 1);
  var thousandsSeparatorSymbol = formattedNumber.substr(formattedNumber.indexOf('1') + 1, 1);
  return {
    prefix: prefix,
    suffix: suffix,
    thousandsSeparatorSymbol: thousandsSeparatorSymbol,
    decimalSymbol: decimalSymbol,
    decimalLimit: decimalLimit,
    allowDecimal: allowDecimal
  }
}

function dispatchEvent (el, eventName, data) {
  var event = document.createEvent('CustomEvent');
  event.initCustomEvent(eventName, true, true, data);
  el.dispatchEvent(event);
}

function elementMatches (el, selector) { return (el.matches || el.msMatchesSelector || el.webkitMatchesSelector).call(el, selector); }

var directive = {
  bind: function bind (el, ref, ref$1) {
    var options = ref.value;
    var context = ref$1.context;
    var inputElement = init(el, options, context.$CI_DEFAULT_OPTIONS || defaultOptions);
    Vue.nextTick(function () {
      if (inputElement.value) {
        applyFixedFractionFormat(inputElement);
      }
    });
    inputElement.addEventListener('input', function () {
      format(inputElement);
    }, { capture: true });
    inputElement.addEventListener('format', function (ref) {
      var detail = ref.detail;
      if (!inputElement.$ci.focus) {
        format(inputElement, detail.value);
      }
    });
    inputElement.addEventListener('focus', function () {
      var ref = inputElement.$ci;
      var options = ref.options;
      var currencyFormatConfig = ref.currencyFormatConfig;
      inputElement.$ci.focus = true;
      if (options.distractionFree) {
        setTimeout(function () {
          var caretPosition = getCaretPosition(inputElement);
          format(inputElement, parse(inputElement.value, currencyFormatConfig));
          inputElement.setSelectionRange(caretPosition, caretPosition);
        }, 0);
      }
    });
    inputElement.addEventListener('blur', function () {
      inputElement.$ci.focus = false;
      applyFixedFractionFormat(inputElement);
    });
  },
  componentUpdated: function componentUpdated (el, ref, ref$1) {
    var value = ref.value;
    var oldValue = ref.oldValue;
    var context = ref$1.context;
    if (!!value && optionsChanged(oldValue, value)) {
      var inputElement = init(el, value, context.$CI_DEFAULT_OPTIONS || defaultOptions);
      applyFixedFractionFormat(inputElement, inputElement.$ci.numberValue);
    }
  }
};
var optionsChanged = function (oldOptions, newOptions) {
  return Object.keys(defaultOptions).some(function (key) { return oldOptions[key] !== newOptions[key]; })
};
var init = function (el, optionsFromBinding, defaultOptions) {
  var inputElement = elementMatches(el, 'input') ? el : el.querySelector('input');
  if (!inputElement) {
    throw new Error('The directive must be applied on an element consists of an input element')
  }
  var options = Object.assign({}, defaultOptions, optionsFromBinding);
  if (options.min != null && options.max != null && options.min > options.max) {
    throw new Error('Invalid number range')
  }
  inputElement.$ci = Object.assign({}, inputElement.$ci || {},
    {options: options,
    currencyFormatConfig: currencyFormatConfig(options.locale, options.currency),
    textMaskInputElement: createTextMaskInputElement({ inputElement: inputElement, mask: [] })});
  return inputElement
};
var applyFixedFractionFormat = function (el, value) {
  if ( value === void 0 ) value = parse(el.value, el.$ci.currencyFormatConfig);
  if (value != null && !el.$ci.currencyFormatConfig.allowDecimal) {
    value = Math.round(value);
  }
  format(el, value);
  dispatchEvent(el, 'input');
};
var format = function (el, value, ref) {
  if ( value === void 0 ) value = el.value;
  if ( ref === void 0 ) ref = el.$ci;
  var options = ref.options;
  var currencyFormatConfig = ref.currencyFormatConfig;
  var textMaskInputElement = ref.textMaskInputElement;
  var focus = ref.focus;
  var hideNegligibleFractionDigits = focus && (options.distractionFree === true || options.distractionFree.hideNegligibleFractionDigits);
  var hideCurrencySymbol = focus && (options.distractionFree === true || options.distractionFree.hideCurrencySymbol);
  var hideThousandsSeparatorSymbol = focus && (options.distractionFree === true || options.distractionFree.hideThousandsSeparatorSymbol);
  if (typeof value === 'number') {
    if (options.min != null && value < options.min) {
      value = options.min;
    }
    if (options.max != null && value > options.max) {
      value = options.max;
    }
    value = new Intl.NumberFormat(options.locale, { minimumFractionDigits: hideNegligibleFractionDigits ? 0 : currencyFormatConfig.decimalLimit }).format(value);
    if (options.distractionFree) {
      value += ' ';
    }
  }
  textMaskInputElement.update(value, {
    inputElement: el,
    pipe: function (conformedValue, ref) {
      var previousConformedValue = ref.previousConformedValue;
      if (options.validateOnInput) {
        if (options.min != null && parse(conformedValue, currencyFormatConfig) < options.min) {
          return previousConformedValue
        }
        if (options.max != null && parse(conformedValue, currencyFormatConfig) > options.max) {
          return previousConformedValue
        }
      }
      return conformedValue
    },
    mask: createCurrencyMask(Object.assign({}, currencyFormatConfig,
      {prefix: hideCurrencySymbol ? '' : currencyFormatConfig.prefix,
      suffix: hideCurrencySymbol ? '' : currencyFormatConfig.suffix,
      thousandsSeparatorSymbol: hideThousandsSeparatorSymbol ? '' : currencyFormatConfig.thousandsSeparatorSymbol,
      allowNegative: (options.min === null && options.max === null) || options.min < 0 || options.max < 0}))
  });
  var numberValue = parse(el.value, currencyFormatConfig);
  el.$ci.numberValue = numberValue;
  dispatchEvent(el, 'format-complete', { numberValue: numberValue });
};
var getCaretPosition = function (el) {
  var ref = el.$ci.currencyFormatConfig;
  var prefix = ref.prefix;
  var thousandsSeparatorSymbol = ref.thousandsSeparatorSymbol;
  var ref$1 = el.$ci.options;
  var distractionFree = ref$1.distractionFree;
  var hideCurrencySymbol = distractionFree === true || distractionFree.hideCurrencySymbol;
  var hideThousandsSeparatorSymbol = distractionFree === true || distractionFree.hideThousandsSeparatorSymbol;
  var position = el.selectionStart;
  if (hideCurrencySymbol) {
    position -= prefix.length;
  }
  if (hideThousandsSeparatorSymbol) {
    var thousandsSeparatorSymbolCount = (el.value.substring(0, el.selectionStart).match(new RegExp(thousandsSeparatorSymbol === '.' ? '\\.' : thousandsSeparatorSymbol, 'g')) || []).length;
    position -= thousandsSeparatorSymbolCount;
  }
  return Math.max(0, position)
};

function objectWithoutProperties (obj, exclude) { var target = {}; for (var k in obj) if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k]; return target; }
var component = {
  render: function render (h) {
    return h('input', {
      domProps: {
        value: this.formattedValue
      },
      directives: [{
        name: 'currency',
        value: this.options
      }],
      on: this.listeners()
    })
  },
  directives: {
    currency: directive
  },
  name: 'CurrencyInput',
  props: {
    value: {
      type: Number,
      default: null
    },
    locale: {
      type: String,
      default: undefined
    },
    currency: {
      type: String,
      default: undefined
    },
    distractionFree: {
      type: [Boolean, Object],
      default: undefined
    },
    min: {
      type: Number,
      default: undefined
    },
    max: {
      type: Number,
      default: undefined
    },
    validateOnInput: {
      type: Boolean,
      default: undefined
    }
  },
  data: function data () {
    return {
      formattedValue: this.value
    }
  },
  computed: {
    options: function options () {
      var this$1 = this;
      var options = Object.assign({}, this.$CI_DEFAULT_OPTIONS || defaultOptions);
      Object.keys(options).forEach(function (key) {
        if (this$1[key] !== undefined) {
          options[key] = this$1[key];
        }
      });
      return options
    }
  },
  watch: {
    value: function value (value$1) {
      dispatchEvent(this.$el, 'format', { value: value$1 });
    }
  },
  methods: {
    listeners: function listeners () {
      var this$1 = this;
      var ref = this.$listeners;
      var input = ref.input;
      var rest = objectWithoutProperties( ref, ["input"] );
      var listeners = rest;
      return Object.assign({}, listeners,
        {'format-complete': function (ref) {
          var detail = ref.detail;
          this$1.$emit('input', detail.numberValue);
          this$1.formattedValue = this$1.$el.value;
        }})
    }
  }
};

var plugin = {
  install: function install (Vue, ref) {
    if ( ref === void 0 ) ref = {};
    var componentName = ref.componentName; if ( componentName === void 0 ) componentName = component.name;
    var directiveName = ref.directiveName; if ( directiveName === void 0 ) directiveName = 'currency';
    var globalOptions = ref.globalOptions; if ( globalOptions === void 0 ) globalOptions = {};
    var options = Object.assign({}, defaultOptions, globalOptions);
    Vue.prototype.$CI_DEFAULT_OPTIONS = options;
    Vue.component(componentName, component);
    Vue.directive(directiveName, directive);
    Vue.prototype.$parseCurrency = function (str, locale, currency) {
      if ( locale === void 0 ) locale = options.locale;
      if ( currency === void 0 ) currency = options.currency;
      return parse(str, currencyFormatConfig(locale, currency));
    };
  }
};

export default plugin;
export { directive as CurrencyDirective, component as CurrencyInput };
